import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { useFetchAllObjectQuery, useFetchObjectByCodeNoCacheMutation } from '@redux/api'

import { mockPrintFormsStore, PrintFormConfig } from '@features/print-forms'

import { ROUTES } from '@constants'
import { AutocompleteOption, CommandParameterDTO, CommandParametersType } from '@types'

import { PrintFormFormValues } from '../types'

const DEFAULT_VALUES: PrintFormFormValues = {
  id: '',
  title: '',
  command: {
    objectCode: null,
    commandCode: null,
    parameters: [],
    body: [],
  },
  paperSize: 'A4',
  orientation: 'portrait',
  documentTemplate: '',
  documentPartials: {},
}

const dtoToFormParam = (dto: CommandParameterDTO): CommandParametersType => ({
  id: dto.id,
  title: dto.title ? { id: dto.title, label: dto.title } : null,
  type: dto.type ? { id: dto.type, label: dto.type } : null,
  objectCode: dto.objectCode ? { id: dto.objectCode, label: dto.objectCode } : null,
  objectTitle: dto.objectTitle,
  value: dto.value,
  pathArray: dto.pathArray,
})

const formParamToDto = (item: CommandParametersType): CommandParameterDTO => ({
  id: item.id,
  title: (item.title?.id as string) ?? '',
  type: (item.type?.id as string) ?? '',
  objectCode: (item.objectCode?.id as string) ?? null,
  objectTitle: item.objectTitle,
  value: item.value,
  pathArray: item.pathArray,
})

const configToFormValues = (config: PrintFormConfig): PrintFormFormValues => ({
  id: config.id,
  title: config.title,
  command: {
    objectCode: config.command.objectCode
      ? { id: config.command.objectCode, label: config.command.objectCode }
      : null,
    commandCode: config.command.commandCode
      ? {
          id: config.command.commandCode,
          label: config.command.commandCode,
          httpMethod: config.command.method,
        }
      : null,
    parameters: config.command.params?.map(dtoToFormParam) ?? [],
    body: config.command.body?.map(dtoToFormParam) ?? [],
  },
  paperSize: typeof config.layout.paperSize === 'string' ? config.layout.paperSize : 'A4',
  orientation: config.layout.orientation,
  documentTemplate: typeof config.documentTemplate === 'string' ? config.documentTemplate : '',
  documentPartials: config.documentPartials ? { ...config.documentPartials } : {},
})

const formValuesToConfig = (values: PrintFormFormValues): PrintFormConfig => {
  const params = values.command.parameters?.map(formParamToDto)
  const body = values.command.body?.map(formParamToDto)

  return {
    id: values.id,
    title: values.title,
    command: {
      objectCode: (values.command.objectCode?.id as string) ?? '',
      commandCode: (values.command.commandCode?.id as string) ?? '',
      method: values.command.commandCode?.httpMethod,
      params,
      body,
    },
    layout: {
      paperSize: values.paperSize,
      orientation: values.orientation,
    },
    documentTemplate: values.documentTemplate,
    documentPartials:
      values.documentPartials && Object.keys(values.documentPartials).length > 0
        ? { ...values.documentPartials }
        : undefined,
  }
}

export const usePrintFormCreateOrEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const methods = useForm<PrintFormFormValues>({ defaultValues: DEFAULT_VALUES })
  const { reset, handleSubmit, control, setValue } = methods

  const watchedValues = useWatch({ control }) as PrintFormFormValues | undefined

  const previewPrintConfig = useMemo((): PrintFormConfig => {
    const values = watchedValues ?? DEFAULT_VALUES

    return formValuesToConfig(values)
  }, [watchedValues])

  const canPrintPreview = Boolean(
    previewPrintConfig.command.objectCode?.trim() &&
      previewPrintConfig.command.commandCode?.trim() &&
      previewPrintConfig.documentTemplate.trim()
  )

  const { data: objects } = useFetchAllObjectQuery()
  const [fetchObjectByCode, { data: selectedObjectData }] = useFetchObjectByCodeNoCacheMutation({})

  const objectsOptions: AutocompleteOption[] =
    objects?.map(obj => ({ id: obj.code, label: obj.title })) ?? []

  const watchObjectCode = useWatch({ control, name: 'command.objectCode' })
  const watchCommandCode = useWatch({ control, name: 'command.commandCode' })

  const selectedObjectCommandsOptions: AutocompleteOption[] =
    watchObjectCode && selectedObjectData?.commands?.length
      ? selectedObjectData.commands.map(cmd => ({
          id: cmd.code,
          label: cmd.code,
          httpMethod: cmd.httpMethod,
        }))
      : []

  useEffect(() => {
    if (watchObjectCode?.id) {
      fetchObjectByCode(watchObjectCode.id as string)
    }
  }, [watchObjectCode?.id, fetchObjectByCode])

  useEffect(() => {
    if (id) {
      const config = mockPrintFormsStore.getById(id)
      if (config) {
        reset(configToFormValues(config))
      }
    }
  }, [id, reset])

  const handleChangeCommand = (option: AutocompleteOption) => {
    setValue('command.commandCode', option, { shouldValidate: true })
    setValue('command.parameters', [])
    setValue('command.body', [])
  }

  const commandReady = Boolean(watchObjectCode?.id && watchCommandCode?.id)

  const handleSave = handleSubmit(values => {
    const submitted = formValuesToConfig(values)

    if (isEdit) {
      const prev = mockPrintFormsStore.getById(values.id)
      const merged: PrintFormConfig =
        prev != null
          ? {
              ...submitted,
              documentTemplate: prev.documentTemplate,
              documentPartials: prev.documentPartials,
            }
          : submitted

      mockPrintFormsStore.update(merged)
    } else {
      mockPrintFormsStore.create({
        ...submitted,
        documentTemplate: '',
        documentPartials: undefined,
      })
    }

    navigate(ROUTES.PRINT_FORMS)
  })

  const handleCancel = () => navigate(-1)

  return {
    methods,
    isEdit,
    commandReady,
    watchObjectCode,
    watchCommandCode,
    objectsOptions,
    selectedObjectCommandsOptions,
    handleChangeCommand,
    handleSave,
    handleCancel,
    previewPrintConfig,
    canPrintPreview,
  }
}
